import { Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import { Dayjs } from 'dayjs';
import { PropsWithChildren, createContext, useContext, useState } from 'react';

import {
  getDefaultTableParams,
  useTableParams,
} from 'business/providers/table-params';
import { DEFAULT_PAGE_SIZE } from 'business/quality-control/constants';
import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';

import { ORDER_FIELD, ORDER_TYPE } from '..';

const FILTERS_FORM_NAME = 'segment-history-filters-form';

const LastScanRange = {
  day: 'day',
  week: 'week',
  month: 'month',
} as const;

const SegmentRange = {
  day: 'day',
  week: 'week',
  month: 'month',
  interval: 'interval',
} as const;

const SegmentDate = {
  manufactureDate: 'manufactureDate',
  surfaceScanDate: 'surfaceScanDate',
  tunnelScanDate: 'tunnelScanDate',
} as const;

export type SegmentHistoryFilters = {
  site: string | null;
  lastScanRange: (typeof LastScanRange)[keyof typeof LastScanRange] | null;
  segmentRange: (typeof SegmentRange)[keyof typeof SegmentRange] | null;
  segmentType: string | null;
  status: Quality_SegmentHistoryStatusEnum_Enum[];
  serialNumber: string | null;
  segmentDate: (typeof SegmentDate)[keyof typeof SegmentDate] | null;
  segmentDateRange: [Dayjs | null, Dayjs | null];
  ringNumberRange: [number | null, number | null];
};

const defaultFilters = {
  site: null,
  lastScanRange: null,
  segmentRange: null,
  segmentType: null,
  status: [],
  serialNumber: null,
  segmentDate: null,
  segmentDateRange: [null, null],
  ringNumberRange: [null, null],
} as const satisfies SegmentHistoryFilters;

const SegmentHistoryFiltersContext =
  createContext<SegmentHistoryFilters>(defaultFilters);

const SegmentHistoryResetFiltersContext = createContext<() => void>(() => {});

const SegmentHistoryFiltersProvider = ({
  initialFilters,
  children,
}: PropsWithChildren<{
  initialFilters: SegmentHistoryFilters;
}>) => {
  const [filters, setFilters] = useState<SegmentHistoryFilters>(initialFilters);
  const { setTableParams } = useTableParams();

  const resetTableParams = () => {
    const defaultTableParams = getDefaultTableParams({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      defaultSortField: ORDER_FIELD,
      defaultSortOrder: ORDER_TYPE,
    });
    setTableParams(defaultTableParams);
  };

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTERS_FORM_NAME) {
      const form = forms[formName];

      const values = {
        ...defaultFilters,
        ...form.getFieldsValue(),
      } satisfies SegmentHistoryFilters;

      setFilters(values);
      form.setFieldsValue(values);

      // Reset pagination, order, etc.
      resetTableParams();
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    resetTableParams();
  };

  return (
    <SegmentHistoryFiltersContext.Provider value={filters}>
      <SegmentHistoryResetFiltersContext.Provider value={resetFilters}>
        <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
      </SegmentHistoryResetFiltersContext.Provider>
    </SegmentHistoryFiltersContext.Provider>
  );
};

const useSegmentHistoryFiltersFormName = () => FILTERS_FORM_NAME;

const useSegmentHistoryFilters = () => {
  return useContext(SegmentHistoryFiltersContext);
};

const useResetSegmentHistoryFilters = () => {
  return useContext(SegmentHistoryResetFiltersContext);
};

export {
  LastScanRange,
  SegmentRange,
  SegmentDate,
  defaultFilters,
  SegmentHistoryFiltersProvider,
  useSegmentHistoryFiltersFormName,
  useSegmentHistoryFilters,
  useResetSegmentHistoryFilters,
};
